/** Открытие окна исходного листа РД с экрана «Позиции объёма» (пункт 9 плана
 *  ПТО): одно окно `osnova_pto_rd_sheet` на все клики, синхронизация через
 *  `BroadcastChannel('osnova_pto_sheet_viewer')` (протокол — `./protocol`).
 *
 *  Первый клик открывает окно `window.open` (синхронно, в обработчике клика —
 *  иначе браузер заблокирует всплывающее окно). Дальше экран шлёт `show`, окно
 *  переключает лист и отвечает `shown`. Окно, открытое другой вкладкой или до
 *  перезагрузки экрана, экран узнаёт по `hello` (ответ на `ping`). Не ответило —
 *  окна нет: следующий клик откроет его заново.
 *
 *  Правка строки в панели окна (№8, выпуск 4) приходит сообщением
 *  `pto_volume_changed`: экран своей ревизии перезапрашивает позиции
 *  ([`useSheetVolumeChanged`]). */
import { useEffect, useState } from 'react';
import {
  SHEET_CHANNEL,
  SHEET_PROTOCOL,
  SHEET_WINDOW_NAME,
  parseSheetMessage,
  sheetWindowUrl,
  type SheetMessage,
  type SheetTarget,
} from './protocol';

/** Окно не ответило на `show` за это время: своё — открыть лист в нём адресом,
 *  чужое (другой вкладки) — считать, что его нет. */
const ACK_TIMEOUT_MS = 3000;
/** Размер нового окна: лист и панель над ним; дальше человек сам ставит окно
 *  на второй монитор, браузер запоминает место. */
const WINDOW_FEATURES = 'popup=yes,width=1280,height=900';

/** Что сообщить человеку после клика. */
export type OpenNotice = 'blocked' | 'lost' | null;

let channel: BroadcastChannel | null = null;
let viewer: Window | null = null;
let viewerAlive = false;
let pending: { msg: SheetMessage; timer: number } | null = null;
const listeners = new Set<(n: OpenNotice) => void>();
/** Подписчики «позиции ревизии изменились в окне листа» (id ревизии). */
const changeListeners = new Set<(revisionId: string) => void>();

function notify(n: OpenNotice) {
  for (const l of listeners) l(n);
}

function post(msg: SheetMessage) {
  channel?.postMessage(msg);
}

function onMessage(e: MessageEvent) {
  const m = parseSheetMessage(e.data);
  if (!m) return;
  if (m.type === 'hello') {
    viewerAlive = true;
    // Окно только что загрузилось (или пришло в себя): последний клик ещё не
    // подтверждён — показать его лист.
    if (pending) post(pending.msg);
  } else if (m.type === 'bye') {
    viewerAlive = false;
  } else if (m.type === 'shown' && pending?.msg.type === 'show' && pending.msg.id === m.id) {
    window.clearTimeout(pending.timer);
    pending = null;
    viewerAlive = true;
  } else if (m.type === 'pto_volume_changed') {
    for (const l of changeListeners) l(m.revisionId);
  }
}

function ensureChannel(): BroadcastChannel | null {
  if (channel || typeof BroadcastChannel === 'undefined') return channel;
  channel = new BroadcastChannel(SHEET_CHANNEL);
  channel.onmessage = onMessage;
  // Окно листа уже открыто (другой вкладкой, до перезагрузки экрана)?
  post({ type: 'ping', v: SHEET_PROTOCOL });
  return channel;
}

function newId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Показать лист в окне листа: переключить открытое или открыть новое.
 *  Вызывать прямо в обработчике клика. */
export function openRdSheet(target: SheetTarget): Exclude<OpenNotice, 'lost'> {
  const ch = ensureChannel();
  const live = !!viewer && !viewer.closed;
  const url = sheetWindowUrl(window.location.origin, target);
  if (ch && (live || viewerAlive)) {
    const msg: SheetMessage = { type: 'show', v: SHEET_PROTOCOL, id: newId(), target };
    if (pending) window.clearTimeout(pending.timer);
    pending = {
      msg,
      timer: window.setTimeout(() => {
        pending = null;
        if (viewer && !viewer.closed) {
          // Своё окно открыто, но не ответило (ещё грузится или ушло со
          // страницы листа) — показать лист в нём адресом.
          try {
            viewer.location.replace(url);
          } catch {
            /* окно недоступно — следующий клик откроет новое */
          }
          return;
        }
        viewerAlive = false;
        viewer = null;
        notify('lost');
      }, ACK_TIMEOUT_MS),
    };
    post(msg);
    if (live) viewer?.focus();
    return null;
  }
  // Окна нет (или канала нет в браузере): открыть. Окно с этим именем, если оно
  // всё же есть, перейдёт на адрес листа — новой вкладки не будет.
  const w = window.open(url, SHEET_WINDOW_NAME, WINDOW_FEATURES);
  if (!w) return 'blocked';
  viewer = w;
  return null;
}

/** Открывашка окна листа для экрана: функция открытия и последнее, что надо
 *  сказать человеку (окно заблокировано браузером или не ответило). */
export function useRdSheetOpener(): {
  open: (target: SheetTarget) => void;
  notice: OpenNotice;
  dismiss: () => void;
} {
  const [notice, setNotice] = useState<OpenNotice>(null);
  useEffect(() => {
    ensureChannel();
    listeners.add(setNotice);
    return () => {
      listeners.delete(setNotice);
    };
  }, []);
  return {
    open: (target) => setNotice(openRdSheet(target)),
    notice,
    dismiss: () => setNotice(null),
  };
}

/** Текст подсказки к `notice`. */
export function noticeText(n: OpenNotice): string | null {
  switch (n) {
    case 'blocked':
      return 'Браузер не дал открыть окно листа: разрешите всплывающие окна для этого сайта и нажмите ещё раз.';
    case 'lost':
      return 'Окно листа не ответило — возможно, его закрыли. Нажмите ещё раз, чтобы открыть его заново.';
    default:
      return null;
  }
}

/** Экран узнаёт о правке строки в панели окна листа (`pto_volume_changed`):
 *  `onChanged` получает id ревизии — экран сверяет его со своей ревизией и
 *  перезапрашивает позиции. */
export function useSheetVolumeChanged(onChanged: (revisionId: string) => void): void {
  useEffect(() => {
    ensureChannel();
    changeListeners.add(onChanged);
    return () => {
      changeListeners.delete(onChanged);
    };
  }, [onChanged]);
}
